import { Modal } from '@react-apps/design-system';
import { useCreateExamQuestion } from '../hooks/use-exams';
import { questionBodyToCreateFields } from '../exam-body';
import { notifyDone, notifyFailed } from '../display';
import { BodyForm } from './BodyForm';

/**
 * Criação manual de questão dentro de uma prova (FAB do preview). O formulário é
 * o `BodyForm` de `QUESTION` que a tela de detalhe de conteúdo já usa — mesmo
 * editor de texto rico, mesma validação, mesma forma de body, mesmo anexo de
 * imagem pelo cursor; aqui ele só é embrulhado num modal e o body vira o payload
 * do endpoint de questão da prova (que além do body leva `position` e as
 * imagens).
 *
 * Não há mapa `imageUrlsByFileId`: questão nova não tem imagem já salva. Nem
 * "anulada": uma questão nasce válida — anular é operação sobre questão que já
 * existe, no card do preview.
 */
export function QuestionFormModal({
  open,
  onClose,
  examId,
  position,
}: {
  open: boolean;
  onClose: () => void;
  examId: string;
  /** Onde a questão entra. Ausente = fim da prova (decidido pelo backend). */
  position?: number;
}) {
  const create = useCreateExamQuestion(examId);

  const close = () => {
    create.reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Criar nova questão"
      description={
        position === undefined
          ? 'A questão nasce como rascunho, no fim da prova.'
          : `A questão nasce como rascunho, na posição ${position}.`
      }
      size="lg"
    >
      {/* `body` vazio: o form abre com dois campos de alternativa em branco.
          A `key` desmonta o form ao fechar — sem isso, reabrir o modal
          traria o rascunho da questão anterior. */}
      <BodyForm
        key={open ? 'open' : 'closed'}
        type="QUESTION"
        body={undefined}
        pending={create.isPending}
        error={create.error}
        onSubmit={(result) => {
          const fields = questionBodyToCreateFields(result.body);
          // Não deveria acontecer (o `build()` do form já validou a forma),
          // mas um `return` mudo aqui fecharia o clique em Salvar sem fechar o
          // modal, sem criar nada e sem dizer nada — o pior feedback possível.
          if (!fields) {
            notifyFailed(
              null,
              'A questão não foi criada — o formulário produziu um corpo que o endpoint não aceita',
            );
            return;
          }
          create.mutate(
            { ...fields, position, images: result.images },
            {
              // A questão nasce no fim de uma prova que pode ter 80 questões,
              // fora da área visível: sem este aviso, "Salvar" apenas fecha o
              // modal e não há nada na tela dizendo que deu certo.
              onSuccess: () => {
                close();
                notifyDone(
                  position === undefined
                    ? 'Questão criada como rascunho, no fim da prova'
                    : `Questão criada como rascunho, na posição ${position}`,
                );
              },
            },
          );
        }}
        onCancel={close}
      />
    </Modal>
  );
}
